// Main compiler pipeline for converting .luis source into CSS output.
// It runs normalize -> preprocess -> parse -> render stages.
import { resolveLuisImports } from './imports'
import { countObservedLeaves, partitionRenderLeaves } from './index-leaves'
import { shouldApplyClassVariableTransform } from './index-transform'
import { normalizeSourceWithDiagnostics } from './normalize'
import { parseLuis } from './parser'
import { preprocessLuisSource } from './preprocess/extract-and-expand-loops'
import { collectRenderLeaves, renderCssFromLeaves } from './renderer'
import { rethrowMappedCompileError } from './error-remap'
import type { LuisCompileMainRule, LuisCompileOptions, LuisCompileResult, LuisCompileWarning } from './types'
import { transformSourceWithClassVariables } from './variables/class-variable'
import { splitHeaderRules } from './output/rule-order'
import { buildRootVariableRule } from './variables/regular-variable'
import { profileLuisSource, resolveCompileMode } from './profile'
import {
  buildResourceLimitWarning,
  resolveResourceLimits,
  resolveResourcePolicy,
  throwResourceLimitError,
} from './resource-limits'

export type {
  LuisCompileMode,
  LuisCompileOptions,
  LuisCompileDebugRule,
  LuisCompileMainRule,
  LuisCompileObservedRule,
  LuisCompileResult,
  LuisCompileWarning,
  LuisImportResolver,
  LuisResolvedImport,
} from './types'
export type { LuisResourceLimits, LuisResourcePolicy } from './resource-limits'

// Build compiled .luis output with CSS and extracted variable metadata.
export function compileLuis(source: string, options: LuisCompileOptions = {}): LuisCompileResult {
  const collectWarnings = options.collectWarnings !== false
  const resourcePolicy = resolveResourcePolicy(options)
  const resourceLimits = resolveResourceLimits(options)
  const rootSourceId = options.sourceId ?? '<inline>'
  const importResolution = resolveLuisImports(source, options)
  try {
    const { source: normalized, warnings: normalizeWarnings } = normalizeSourceWithDiagnostics(importResolution.source, collectWarnings)
    const sourceProfile = profileLuisSource(normalized)
    const resolvedMode = resolveCompileMode(sourceProfile, options.compileMode)
    const { source: preprocessedSource, variables, classVariables, objectVariables, arrayVariables, warnings: preprocessWarnings } =
      preprocessLuisSource(normalized, collectWarnings, resolvedMode.mode, {
        sourceId: rootSourceId,
        resourcePolicy,
        resourceLimits,
      })
    const sourceWithClassTransforms = shouldApplyClassVariableTransform(
      resolvedMode.mode,
      classVariables,
      objectVariables,
      arrayVariables,
    )
      ? transformSourceWithClassVariables(preprocessedSource, {
        classVariables,
        variables,
        objectVariables,
        arrayVariables,
      })
      : preprocessedSource
    const root = parseLuis(sourceWithClassTransforms, objectVariables, 1, arrayVariables)
    const renderWarnings: LuisCompileWarning[] = []
    let leaves = collectRenderLeaves(root, renderWarnings)
    const resourceWarnings: LuisCompileWarning[] = []
  // Check the total rendered-rule budget before CSS generation.
    if (leaves.length > resourceLimits.maxExpandedRules) {
      const message = `[${rootSourceId}] Rule budget exceeded (${leaves.length}/${resourceLimits.maxExpandedRules}).`
      if (resourcePolicy === 'fail-closed') {
        throwResourceLimitError(message)
      } else {
        resourceWarnings.push(buildResourceLimitWarning(1, message))
        leaves = leaves.slice(0, resourceLimits.maxExpandedRules)
      }
    } else {
      // Continue render flow when total rule count is within budget.
    }

    const observedLeafCount = countObservedLeaves(leaves)
    // Check the observed-rule budget before runtime payload generation.
    if (observedLeafCount > resourceLimits.maxObservedRules) {
      const message = `[${rootSourceId}] Observed rule budget exceeded (${observedLeafCount}/${resourceLimits.maxObservedRules}).`
      if (resourcePolicy === 'fail-closed') {
        throwResourceLimitError(message)
      } else {
        resourceWarnings.push(buildResourceLimitWarning(1, message))
        let observedSeen = 0
        const allowedObserved = new Set<string>()
        // Start one observed-leaf allowance scan.
        for (const leaf of leaves) {
          if (leaf.observedMode !== 'none') {
            if (observedSeen < resourceLimits.maxObservedRules) {
              allowedObserved.add(leaf.key)
            }
            observedSeen += 1
          }
        }
        leaves = leaves.filter((leaf) => leaf.observedMode === 'none' || allowedObserved.has(leaf.key))
      }
    } else {
      // Continue render flow when observed rule count is within budget.
    }

    // Build grouped leaves for regular CSS and observed runtime payloads.
    const { regularLeaves, mainRules, observedRules, initialObservedRules } = partitionRenderLeaves(leaves)
    const cssRules = renderCssFromLeaves(root.declarations, regularLeaves)
    // Build only regular CSS variables into `:root`.
    const rootVariableRule = buildRootVariableRule(variables)
    const [headerRules, bodyRules] = splitHeaderRules(cssRules)

    const cssOutput = [...headerRules, rootVariableRule, ...bodyRules].filter(Boolean).join('\n\n')
    // Check the final CSS-size budget after render assembly.
    if (cssOutput.length > resourceLimits.maxOutputCssChars) {
      const message = `[${rootSourceId}] CSS size budget exceeded (${cssOutput.length}/${resourceLimits.maxOutputCssChars} chars).`
      if (resourcePolicy === 'fail-closed') {
        throwResourceLimitError(message)
      } else {
        resourceWarnings.push(buildResourceLimitWarning(1, message))
      }
    } else {
      // Continue result creation when css size is within budget.
    }
    // Return compiled CSS output with grouped rules and diagnostics.
    return {
      css: cssOutput,
      headerRules,
      rootVariableRule,
      mainRules,
      variables,
      classVariables,
      objectVariables,
      arrayVariables,
      warnings: collectWarnings ? [...importResolution.warnings, ...normalizeWarnings, ...preprocessWarnings, ...renderWarnings, ...resourceWarnings] : [],
      observedRules,
      initialObservedRules,
    }
  } catch (error) {
    rethrowMappedCompileError(error, importResolution.lineMap, rootSourceId)
  }
}
